/**
 * Governance → "Create my PPP" (contract: docs/CONTRACT-ppp-build-clients.md §7). The console imports nothing from the
 * service, so the shapes below mirror `services/gyro-svc/src/ppp/pppTypes.ts` field for field (`pppSettingsSchema`,
 * `pppSettingsPatchSchema`, `pppAdminStatusSchema`); `test/ppp.test.ts` pins `PPP_SETTINGS_KEYS` against a literal list so
 * drift between the two is caught. No React here: the form arithmetic is unit-tested on its own.
 */

export type PppEligibility = 'everyone' | 'allowlist' | 'group';
export type PppRunPhase = 'created' | 'gathering' | 'gathered' | 'drafting' | 'review' | 'exported' | 'aborted';

/** = `PppSettings` (pppTypes.ts). */
export interface PppAdminSettings {
  eligibility: PppEligibility;
  allowlist: string[];
  groupId?: string;
  maxUsdPerRun: number;
  runsPerUserPerDay: number;
  maxDeviceCallsPerGather: number;
  dispatchBudgetMs: number;
  retentionMonths: number;
  holidays: string[];
  allowDiscretionWaiver: boolean;
  sensitivityLabelId?: string;
  leadership: string[];
  departmentAliases: Record<string, string>;
  reminders: { desktopPush: boolean; viaBridge: boolean; daysBefore: number; hour: number };
}

/** = `PppSettingsPatch` (pppTypes.ts): every field optional; `null` clears `groupId` and `sensitivityLabelId`. */
export interface PppAdminPatch {
  eligibility?: PppEligibility;
  allowlist?: string[];
  groupId?: string | null;
  maxUsdPerRun?: number;
  runsPerUserPerDay?: number;
  maxDeviceCallsPerGather?: number;
  dispatchBudgetMs?: number;
  retentionMonths?: number;
  holidays?: string[];
  allowDiscretionWaiver?: boolean;
  sensitivityLabelId?: string | null;
  leadership?: string[];
  departmentAliases?: Record<string, string>;
  reminders?: { desktopPush?: boolean; viaBridge?: boolean; daysBefore?: number; hour?: number };
}

/** = `PppAdminStatus` (pppTypes.ts): what all three admin routes answer. */
export interface PppAdminView {
  settings: PppAdminSettings;
  defaults: PppAdminSettings;
  flag?: { enabled: boolean; percent?: number };
  counts: {
    reportsStored: number;
    runsToday: number;
    runsInProgress: { count: number; oldestStartedAt?: string; phases: Partial<Record<PppRunPhase, number>> };
    shareGrants: number;
    digests: number;
    remindersQueuedToday: number;
  };
  sweep: { lastAt?: string; lastRemoved: number };
  bridge: { reminders: boolean; teamsRuns: boolean; groupEligibility: boolean };
  /** 'durable': a database or Azure Blob; 'blob' (when the service names it) is the same; 'json': local files only. */
  storage: 'durable' | 'json' | 'blob';
}

export const PPP_SETTINGS_KEYS: readonly (keyof PppAdminSettings)[] = [
  'eligibility',
  'allowlist',
  'groupId',
  'maxUsdPerRun',
  'runsPerUserPerDay',
  'maxDeviceCallsPerGather',
  'dispatchBudgetMs',
  'retentionMonths',
  'holidays',
  'allowDiscretionWaiver',
  'sensitivityLabelId',
  'leadership',
  'departmentAliases',
  'reminders',
];

/** The sentence under the `ppp-report` flag in the generic Feature flags table (Governance.tsx FLAG_HELP). */
export const PPP_FLAG_HELP = 'Create my PPP on the desktop app and in the Office add-in. Off until stored on at percent 100, off when disabled; roles cannot be used (desktop and Office callers carry none) and there is no percent ramp.';

/** What the screen edits: every stored field as text or a switch (lists one entry per line; the budget in seconds). */
export interface PppForm {
  eligibility: PppEligibility;
  allowlist: string;
  groupId: string;
  maxUsdPerRun: string;
  runsPerUserPerDay: string;
  maxDeviceCallsPerGather: string;
  dispatchBudgetSeconds: string;
  retentionMonths: string;
  holidays: string;
  allowDiscretionWaiver: boolean;
  sensitivityLabelId: string;
  leadership: string;
  departmentAliases: string;
  remindersDesktopPush: boolean;
  remindersViaBridge: boolean;
  remindersDaysBefore: string;
  remindersHour: string;
}

/** `people`: one address per line, edited with the people picker (components/PeoplePicker.tsx). */
export type PppFieldKind = 'radio' | 'lines' | 'people' | 'text' | 'number' | 'switch' | 'aliases';

export interface PppField {
  key: keyof PppForm;
  /** The plain name on screen (never the setting's identifier). */
  label: string;
  hint: string;
  kind: PppFieldKind;
  /** Numbers: the schema's bounds (seconds for the gathering time); lines and people: the maximum count. */
  bounds?: { min: number; max: number };
  /** Numbers: a whole number only. */
  integer?: boolean;
  /** The switch needs The Bridge connected (shown with a note while `bridge.*` is false). */
  bridge?: keyof PppAdminView['bridge'];
}

export const PPP_FIELDS: readonly PppField[] = [
  { key: 'eligibility', label: 'Who can build a PPP', kind: 'radio', hint: 'Everyone (owner, 2026-10-07). Allow-list is for a smoke check or to pause a person, not a pilot gate.' },
  { key: 'allowlist', label: 'Allow-list', kind: 'people', bounds: { min: 0, max: 2000 }, hint: 'Used only with Allow-list. Search the people Gyro knows, or type or paste full e-mail addresses.' },
  { key: 'groupId', label: 'Entra group', kind: 'text', hint: 'Entra group object id; resolved through The Bridge only; needs GYRO_PPP_GROUP_VIA_BRIDGE=1 (off).' },
  { key: 'maxUsdPerRun', label: 'Dollars per report', kind: 'number', bounds: { min: 0, max: 100 }, hint: 'Default 3.' },
  { key: 'runsPerUserPerDay', label: 'Reports per person per day', kind: 'number', bounds: { min: 0, max: 50 }, integer: true, hint: 'Default 3; the fourth run today is refused.' },
  { key: 'maxDeviceCallsPerGather', label: 'Microsoft 365 reads per turn', kind: 'number', bounds: { min: 1, max: 24 }, integer: true, hint: 'Default 24 per gather turn; the standard plan reads 27 to 57 over two or three turns, paging each source; the channel never allows more than 24 per turn.' },
  { key: 'dispatchBudgetSeconds', label: 'Gathering time (seconds)', kind: 'number', bounds: { min: 60, max: 540 }, integer: true, hint: 'Default 360 s.' },
  { key: 'retentionMonths', label: 'Months reports are kept', kind: 'number', bounds: { min: 1, max: 36 }, integer: true, hint: 'Default 13 (owner). Reports older than this are deleted by the sweep.' },
  { key: 'holidays', label: 'Company holidays', kind: 'lines', bounds: { min: 0, max: 400 }, hint: 'Company holidays, for business-day arithmetic. One YYYY-MM-DD per line.' },
  { key: 'allowDiscretionWaiver', label: 'Allow the HR/legal specifics waiver', kind: 'switch', hint: "A person may say 'my audience is authorized for HR specifics' for one report (owner, 2026-10-07); audited." },
  { key: 'sensitivityLabelId', label: 'Sensitivity label', kind: 'text', hint: 'Idle. The owner chose footer only; set a label id to stamp the .docx.' },
  { key: 'leadership', label: 'Leadership digest readers', kind: 'people', bounds: { min: 0, max: 200 }, hint: 'Who may read the leadership digest. Search the people Gyro knows, or type or paste full e-mail addresses.' },
  { key: 'departmentAliases', label: 'Department aliases', kind: 'aliases', hint: "So 'BIM' and 'BIM Technologies' sort together. One alias = canonical per line." },
  { key: 'remindersDesktopPush', label: 'Desktop reminder', kind: 'switch', hint: "Signed reminder to the person's Gyro desktop app (off by default)." },
  { key: 'remindersViaBridge', label: 'Reminder through The Bridge', kind: 'switch', bridge: 'reminders', hint: 'E-mail or Teams nudge through The Bridge → Star Command; also needs GYRO_PPP_REMINDERS_VIA_BRIDGE=1 (off).' },
  { key: 'remindersDaysBefore', label: 'Reminder, days before', kind: 'number', bounds: { min: 0, max: 6 }, integer: true, hint: "Default 1 day before, 09:00 in the person's zone." },
  { key: 'remindersHour', label: 'Reminder hour', kind: 'number', bounds: { min: 0, max: 23 }, integer: true, hint: "Default 1 day before, 09:00 in the person's zone." },
];

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const EMAIL = /^[^\s@]+@[^\s@]+$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function lines(value: string): string[] {
  return value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
}

function aliasesText(aliases: Record<string, string>): string {
  return Object.keys(aliases).sort().map((alias) => `${alias} = ${aliases[alias]}`).join('\n');
}

/** `alias = canonical` lines → the map; a line without `=` or with an empty side is reported by validatePpp. */
export function parseAliases(value: string): { ok: true; aliases: Record<string, string> } | { ok: false; line: string } {
  const aliases: Record<string, string> = {};
  for (const line of lines(value)) {
    const at = line.indexOf('=');
    if (at < 0) return { ok: false, line };
    const alias = line.slice(0, at).trim();
    const canonical = line.slice(at + 1).trim();
    if (!alias || !canonical || alias.length > 120 || canonical.length > 120) return { ok: false, line };
    aliases[alias] = canonical;
  }
  return { ok: true, aliases };
}

/** The stored settings as the screen edits them. */
export function pppForm(settings: PppAdminSettings): PppForm {
  return {
    eligibility: settings.eligibility,
    allowlist: settings.allowlist.join('\n'),
    groupId: settings.groupId ?? '',
    maxUsdPerRun: String(settings.maxUsdPerRun),
    runsPerUserPerDay: String(settings.runsPerUserPerDay),
    maxDeviceCallsPerGather: String(settings.maxDeviceCallsPerGather),
    dispatchBudgetSeconds: String(Math.round(settings.dispatchBudgetMs / 1000)),
    retentionMonths: String(settings.retentionMonths),
    holidays: settings.holidays.join('\n'),
    allowDiscretionWaiver: settings.allowDiscretionWaiver,
    sensitivityLabelId: settings.sensitivityLabelId ?? '',
    leadership: settings.leadership.join('\n'),
    departmentAliases: aliasesText(settings.departmentAliases),
    remindersDesktopPush: settings.reminders.desktopPush,
    remindersViaBridge: settings.reminders.viaBridge,
    remindersDaysBefore: String(settings.reminders.daysBefore),
    remindersHour: String(settings.reminders.hour),
  };
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function sameAliases(a: Record<string, string>, b: Record<string, string>): boolean {
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  return sameList(ka, kb) && ka.every((key) => a[key] === b[key]);
}

/**
 * Only what changed against the stored settings, in the shape `PUT /v1/admin/ppp` takes; a cleared `groupId` or
 * `sensitivityLabelId` is sent as `null`. Call validatePpp first: this assumes the form is sound.
 */
export function pppPatch(form: PppForm, stored: PppAdminSettings): PppAdminPatch {
  const patch: PppAdminPatch = {};
  if (form.eligibility !== stored.eligibility) patch.eligibility = form.eligibility;
  const allowlist = lines(form.allowlist).map((line) => line.toLowerCase());
  if (!sameList(allowlist, stored.allowlist)) patch.allowlist = allowlist;
  const groupId = form.groupId.trim().toLowerCase();
  if (groupId !== (stored.groupId ?? '')) patch.groupId = groupId || null;
  const numbers: Array<['maxUsdPerRun' | 'runsPerUserPerDay' | 'maxDeviceCallsPerGather' | 'retentionMonths', string]> = [
    ['maxUsdPerRun', form.maxUsdPerRun],
    ['runsPerUserPerDay', form.runsPerUserPerDay],
    ['maxDeviceCallsPerGather', form.maxDeviceCallsPerGather],
    ['retentionMonths', form.retentionMonths],
  ];
  for (const [key, text] of numbers) {
    const value = Number(text);
    if (value !== stored[key]) patch[key] = value;
  }
  const budget = Math.round(Number(form.dispatchBudgetSeconds) * 1000);
  if (budget !== stored.dispatchBudgetMs) patch.dispatchBudgetMs = budget;
  const holidays = lines(form.holidays);
  if (!sameList(holidays, stored.holidays)) patch.holidays = holidays;
  if (form.allowDiscretionWaiver !== stored.allowDiscretionWaiver) patch.allowDiscretionWaiver = form.allowDiscretionWaiver;
  const label = form.sensitivityLabelId.trim().toLowerCase();
  if (label !== (stored.sensitivityLabelId ?? '')) patch.sensitivityLabelId = label || null;
  const leadership = lines(form.leadership).map((line) => line.toLowerCase());
  if (!sameList(leadership, stored.leadership)) patch.leadership = leadership;
  const aliases = parseAliases(form.departmentAliases);
  if (aliases.ok && !sameAliases(aliases.aliases, stored.departmentAliases)) patch.departmentAliases = aliases.aliases;
  const reminders: NonNullable<PppAdminPatch['reminders']> = {};
  if (form.remindersDesktopPush !== stored.reminders.desktopPush) reminders.desktopPush = form.remindersDesktopPush;
  if (form.remindersViaBridge !== stored.reminders.viaBridge) reminders.viaBridge = form.remindersViaBridge;
  const daysBefore = Number(form.remindersDaysBefore);
  if (daysBefore !== stored.reminders.daysBefore) reminders.daysBefore = daysBefore;
  const hour = Number(form.remindersHour);
  if (hour !== stored.reminders.hour) reminders.hour = hour;
  if (Object.keys(reminders).length) patch.reminders = reminders;
  return patch;
}

function fieldLabel(key: keyof PppForm): string {
  return PPP_FIELDS.find((field) => field.key === key)?.label ?? key;
}

function checkNumber(key: keyof PppForm, text: string): string | null {
  const field = PPP_FIELDS.find((entry) => entry.key === key);
  const bounds = field?.bounds ?? { min: 0, max: Number.MAX_SAFE_INTEGER };
  const value = Number(text);
  if (text.trim() === '' || !Number.isFinite(value)) return `${fieldLabel(key)}: enter a number from ${bounds.min} to ${bounds.max}.`;
  if (field?.integer && !Number.isInteger(value)) return `${fieldLabel(key)}: enter a whole number from ${bounds.min} to ${bounds.max}.`;
  if (value < bounds.min || value > bounds.max) return `${fieldLabel(key)}: must be from ${bounds.min} to ${bounds.max}.`;
  return null;
}

/** The first violation the service would refuse, naming the field; null when the form can be sent. */
export function validatePpp(form: PppForm): string | null {
  const allowlist = lines(form.allowlist);
  if (allowlist.length > 2000) return `${fieldLabel('allowlist')}: at most 2,000 addresses.`;
  const badAddress = allowlist.find((line) => !EMAIL.test(line) || line.length > 320);
  if (badAddress) return `${fieldLabel('allowlist')}: "${badAddress}" is not an e-mail address.`;
  if (form.eligibility === 'allowlist' && allowlist.length === 0) return `${fieldLabel('allowlist')}: add at least one address, or choose Everyone.`;
  const groupId = form.groupId.trim().toLowerCase();
  if (groupId && !GUID.test(groupId)) return `${fieldLabel('groupId')}: enter an Entra group object id (a GUID) or leave it blank.`;
  if (form.eligibility === 'group' && !groupId) return `${fieldLabel('groupId')}: an Entra group id is needed for group eligibility.`;
  for (const key of ['maxUsdPerRun', 'runsPerUserPerDay', 'maxDeviceCallsPerGather', 'dispatchBudgetSeconds', 'retentionMonths', 'remindersDaysBefore', 'remindersHour'] as const) {
    const problem = checkNumber(key, form[key]);
    if (problem) return problem;
  }
  const holidays = lines(form.holidays);
  if (holidays.length > 400) return `${fieldLabel('holidays')}: at most 400 days.`;
  const badDay = holidays.find((line) => !DATE.test(line));
  if (badDay) return `${fieldLabel('holidays')}: "${badDay}" is not a YYYY-MM-DD date.`;
  const label = form.sensitivityLabelId.trim().toLowerCase();
  if (label && !GUID.test(label)) return `${fieldLabel('sensitivityLabelId')}: enter a label id (a GUID) or leave it blank.`;
  const leadership = lines(form.leadership);
  if (leadership.length > 200) return `${fieldLabel('leadership')}: at most 200 addresses.`;
  const badLeader = leadership.find((line) => !EMAIL.test(line) || line.length > 320);
  if (badLeader) return `${fieldLabel('leadership')}: "${badLeader}" is not an e-mail address.`;
  const aliases = parseAliases(form.departmentAliases);
  if (!aliases.ok) return `${fieldLabel('departmentAliases')}: "${aliases.line}" is not "alias = canonical".`;
  return null;
}

/** The status panel's first line: what is stored, how it is kept, and the flag. */
export function pppStatusText(view: PppAdminView): string {
  const storage = view.storage === 'durable' ? 'kept in the durable store'
    : view.storage === 'blob' ? 'kept in Azure Blob (durable)'
      : 'kept as JSON (not durable: a restart loses runs)';
  const flag = view.flag ? (view.flag.enabled ? `flag on${typeof view.flag.percent === 'number' && view.flag.percent < 100 ? ` at ${view.flag.percent}%` : ''}` : 'flag off') : 'flag not stored (off)';
  const c = view.counts;
  const sweep = view.sweep.lastAt ? `last sweep ${view.sweep.lastAt} removed ${view.sweep.lastRemoved}` : 'no sweep yet';
  return `${c.reportsStored} report(s) stored, ${c.runsToday} run(s) today, ${c.shareGrants} share grant(s), ${c.digests} digest(s), ${c.remindersQueuedToday} reminder(s) today; ${storage}; ${flag}; ${sweep}.`;
}

/** The line the deploy runbook reads before a gyro-svc deploy (P12). */
export function runsInProgressText(view: PppAdminView, now: number = Date.now()): string {
  const r = view.counts.runsInProgress;
  if (r.count === 0) return 'No PPP run in progress';
  const started = r.oldestStartedAt ? Date.parse(r.oldestStartedAt) : Number.NaN;
  const age = Number.isFinite(started) ? formatAge(started, now) : 'unknown';
  return `${r.count} PPP run(s) in progress (oldest started ${age}); wait for export when you can`;
}

/** governance.ts formatAge, repeated here so this module stays import-free for its unit test. */
export function formatAge(at: number | undefined, now: number): string {
  if (typeof at !== 'number' || !Number.isFinite(at) || at <= 0) return 'unknown';
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** The three Bridge-dependent switches, each `live` or `off`, for the status panel. */
export function bridgeLines(view: PppAdminView): string[] {
  const word = (on: boolean) => (on ? 'live' : 'off');
  return [
    `Reminders through The Bridge: ${word(view.bridge.reminders)}`,
    `Teams-initiated runs through The Bridge: ${word(view.bridge.teamsRuns)}`,
    `Group eligibility through The Bridge: ${word(view.bridge.groupEligibility)}`,
  ];
}

/** Why the card could not be read, as a sentence (never defaults shown as if they were stored). */
export function pppReadFailure(err: unknown): string {
  const status = (err as { status?: unknown } | null | undefined)?.status;
  if (status === 404) return 'This service has no "Create my PPP" routes yet.';
  if (status === 401) return 'Sign in again to read the PPP settings.';
  if (status === 403) return 'Your account may not read the PPP settings.';
  if (status === 503) return 'The PPP settings could not be read right now (storage unavailable); try again in a minute.';
  const message = err instanceof Error && err.message.trim() ? err.message : '';
  return message ? `The PPP settings could not be read: ${message}` : 'The PPP settings could not be read.';
}
